import { FC, useState, useEffect } from 'react';
import { useAppConfig } from '../context/AppConfigContext';
import '../glass-styles.css'; // Import our dedicated CSS file

interface GlassMemberListPageProps {
  isFilterPanelOpen: boolean;
  onFilterToggle: () => void;
  updateActiveFilters?: (active: boolean) => void;
}

const GlassMemberListPage: FC<GlassMemberListPageProps> = ({ isFilterPanelOpen, onFilterToggle, updateActiveFilters }) => {
  // Import AppConfig context but don't use it directly
  useAppConfig();

  // Example of how to use the updateActiveFilters in the glass version
  useEffect(() => {
    // This is just a placeholder. In a real implementation, 
    // we would base this on actual filter state
    if (updateActiveFilters) {
      updateActiveFilters(false);
    }
  }, [updateActiveFilters]);

  // Tab state for each member card
  const [activeTabMap, setActiveTabMap] = useState<Record<string, 'summary' | 'profile'>>({
    '12345': 'summary',
    '67890': 'summary',
  });

  // Set active tab for a specific member
  const setActiveTab = (memberId: string, tab: 'summary' | 'profile') => {
    setActiveTabMap(prev => ({
      ...prev,
      [memberId]: tab,
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-blue/80 to-brand-purple/90 bg-fixed">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4">
        {/* Filter Toggle Button */}
        <button
          onClick={onFilterToggle}
          className={`mb-4 px-4 py-2 rounded-lg text-white bg-brand-blue/20 hover:bg-brand-blue/30 transition-colors ${
            isFilterPanelOpen ? 'bg-brand-blue/30' : ''
          }`}
        >
          {isFilterPanelOpen ? 'Hide Filters' : 'Show Filters'}
        </button>
        
        {/* Sample Content with Glassmorphic Cards */}
        <div className="grid grid-cols-1 gap-4">
          {/* Sample Card 1 */}
          <div
            className="glass-card"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderColor: 'rgba(255, 255, 255, 0.2)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
              }}
            >
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'white' }}>
                John Smith
              </h3>
              <div style={{ display: 'flex' }}>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color:
                      activeTabMap['12345'] === 'summary' ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                    borderBottom:
                      activeTabMap['12345'] === 'summary' ? '2px solid #ffffff' : 'none',
                  }}
                  onClick={() => setActiveTab('12345', 'summary')}
                >
                  Summary
                </button>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color:
                      activeTabMap['12345'] === 'profile' ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                    borderBottom:
                      activeTabMap['12345'] === 'profile' ? '2px solid #ffffff' : 'none',
                  }}
                  onClick={() => setActiveTab('12345', 'profile')}
                >
                  Profile
                </button>
              </div>
            </div>

            {activeTabMap['12345'] === 'summary' ? (
              <>
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '0.25rem',
                  }}
                >
                  Member ID: 12345
                </div>
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '0.25rem',
                  }}
                >
                  Account Status: Active
                </div>
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '0.75rem',
                  }}
                >
                  Loan Status: Current
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '1rem',
                  }}
                >
                  <div
                    className="glass-badge"
                    style={{
                      backgroundColor: 'rgba(59, 130, 246, 0.3)',
                      color: 'white',
                      borderColor: 'rgba(59, 130, 246, 0.5)',
                    }}
                  >
                    Score: 85
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="glass-button"
                      style={{ backgroundColor: 'rgba(59, 130, 246, 0.7)' }}
                    >
                      Assist
                    </button>
                    <button
                      className="glass-button-secondary"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      }}
                    >
                      Email
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div
                className="glass-profile-card"
                style={{
                  padding: '1.5rem',
                  borderRadius: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow:
                    '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.15)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Decorative glassmorphic elements */}
                <div
                  style={{
                    position: 'absolute',
                    top: '-80px',
                    right: '-80px',
                    width: '160px',
                    height: '160px',
                    borderRadius: '50%',
                    background:
                      'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0) 70%)',
                    filter: 'blur(20px)',
                    zIndex: 0,
                  }}
                ></div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-60px',
                    left: '-60px',
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background:
                      'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0) 70%)',
                    filter: 'blur(20px)',
                    zIndex: 0,
                  }}
                ></div>

                {/* Content with enhanced glass effect */}
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <h4
                    style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: 'white',
                      marginBottom: '1.25rem',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      paddingBottom: '0.5rem',
                    }}
                  >
                    Member Profile
                  </h4>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '1.5rem',
                    }}
                  >
                    <div
                      style={{
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Full Name
                        </p>
                        <p style={{ color: 'white', fontWeight: '500' }}>John Smith</p>
                      </div>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Email
                        </p>
                        <p style={{ color: 'white' }}>john.smith@example.com</p>
                      </div>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Phone
                        </p>
                        <p style={{ color: 'white' }}>555-123-4567</p>
                      </div>
                    </div>
                    <div
                      style={{
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Address
                        </p>
                        <p style={{ color: 'white' }}>123 Main St, Anytown, CA 12345</p>
                      </div>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Member Since
                        </p>
                        <p style={{ color: 'white' }}>Jan 15, 2022</p>
                      </div>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Yearly Income
                        </p>
                        <p style={{ color: 'white' }}>$75,000</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sample Card 2 */}
          <div
            className="glass-card"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderColor: 'rgba(255, 255, 255, 0.2)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
              }}
            >
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'white' }}>Jane Doe</h3>
              <div style={{ display: 'flex' }}>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color:
                      activeTabMap['67890'] === 'summary' ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                    borderBottom:
                      activeTabMap['67890'] === 'summary' ? '2px solid #ffffff' : 'none',
                  }}
                  onClick={() => setActiveTab('67890', 'summary')}
                >
                  Summary
                </button>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color:
                      activeTabMap['67890'] === 'profile' ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                    borderBottom:
                      activeTabMap['67890'] === 'profile' ? '2px solid #ffffff' : 'none',
                  }}
                  onClick={() => setActiveTab('67890', 'profile')}
                >
                  Profile
                </button>
              </div>
            </div>

            {activeTabMap['67890'] === 'summary' ? (
              <>
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '0.25rem',
                  }}
                >
                  Member ID: 67890
                </div>
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '0.25rem',
                  }}
                >
                  Account Status: Active
                </div>
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '0.75rem',
                  }}
                >
                  Loan Status: Current
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '1rem',
                  }}
                >
                  <div
                    className="glass-badge"
                    style={{
                      backgroundColor: 'rgba(16, 185, 129, 0.3)',
                      color: 'white',
                      borderColor: 'rgba(16, 185, 129, 0.5)',
                    }}
                  >
                    Score: 92
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="glass-button"
                      style={{ backgroundColor: 'rgba(59, 130, 246, 0.7)' }}
                    >
                      Assist
                    </button>
                    <button
                      className="glass-button-secondary"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      }}
                    >
                      Email
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div
                className="glass-profile-card"
                style={{
                  padding: '1.5rem',
                  borderRadius: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow:
                    '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.15)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Decorative glassmorphic elements */}
                <div
                  style={{
                    position: 'absolute',
                    top: '-80px',
                    right: '-80px',
                    width: '160px',
                    height: '160px',
                    borderRadius: '50%',
                    background:
                      'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0) 70%)',
                    filter: 'blur(20px)',
                    zIndex: 0,
                  }}
                ></div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-60px',
                    left: '-60px',
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background:
                      'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0) 70%)',
                    filter: 'blur(20px)',
                    zIndex: 0,
                  }}
                ></div>

                {/* Content with enhanced glass effect */}
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <h4
                    style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: 'white',
                      marginBottom: '1.25rem',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      paddingBottom: '0.5rem',
                    }}
                  >
                    Member Profile
                  </h4>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '1.5rem',
                    }}
                  >
                    <div
                      style={{
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Full Name
                        </p>
                        <p style={{ color: 'white', fontWeight: '500' }}>Jane Doe</p>
                      </div>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Email
                        </p>
                        <p style={{ color: 'white' }}>jane.doe@example.com</p>
                      </div>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Phone
                        </p>
                        <p style={{ color: 'white' }}>555-987-6543</p>
                      </div>
                    </div>
                    <div
                      style={{
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Address
                        </p>
                        <p style={{ color: 'white' }}>456 Oak Ave, Somewhere, NY 54321</p>
                      </div>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Member Since
                        </p>
                        <p style={{ color: 'white' }}>Mar 22, 2021</p>
                      </div>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.25rem',
                          }}
                        >
                          Yearly Income
                        </p>
                        <p style={{ color: 'white' }}>$92,000</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Pagination info */}
          <div
            style={{ marginTop: '1.5rem', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Showing 2 sample members
          </div>
        </div>

        {/* Glassmorphic Action Button */}
        <button
          className="glass-action-button"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}
          aria-label="Scroll to top"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      </main>
    </div>
  );
};

export default GlassMemberListPage;
